import React, {useMemo} from 'react';
import {faAngleLeft, faAngleRight} from '@netx/core-icons';
import Dialog, {type DialogProps} from '@netx/core-components/Dialog';
import {unescapeDelimiters} from '@netx/core-next/utils';
import {IconButton} from '@netx/core-components/IconButton';
import {setViewData} from '../../api';
import {AssetViewEvent} from '../../actions';
import {
	composeClasses,
	styled,
	type WithClasses,
} from '@netx/core-components/Theme';
import {type IAssetView} from '../../schema';
import {useAssetViewsProvider} from '../../contexts';
import {AssetViewsDetail, type AssetViewsDetailProps} from './AssetViewsDetail';
import {useAssetViewForm, useAssetViewActions} from '../../hooks';

export type AssetViewsDetailDialogRef = React.ForwardedRef<HTMLDivElement>;

type AssetViewsDetailUtiltiyClassProps = WithClasses<AssetViewsDetailProps, {}>;

// const useUtilityClasses = (ownerState: AssetViewsDetailUtiltiyClassProps) => {
//     const {classes} = ownerState;
//     const slots = {
//         root: ['root'],
//         appBar: ['appBar'],
//         content: ['content'],
//         paper: ['paper'],
//         preview: ['preview'],
//         previewWrapper: ['previewWrapper'],
//         toolbar: ['toolbar'],
//     };
//     return composeClasses(slots, getAssetViewsDetailDialogUtilityClass, classes);
// };

const AssetViewsDetailDialogRoot = styled(Dialog, {
	//name: ASSET_PREVIEW_DIALOG_CLASS_NAME,
	// slot: 'Root',
})(({theme}) => ({
	// Want other dialogs to be on top this dialog
	// @ts-ignore
	zIndex: `1038 !important`, // `${theme.zIndex.modal - 15} !important`,
	[theme.breakpoints.up('sm')]: {
		'& > .MuiDialog-container > .MuiPaper-root': {
			// margin: 16,
			// maxWidth: 'initial',
			// width: `calc(100% - 32px)`,
			// height: `calc(100% - 32px)`,
			height: 500,
		},
	},
}));

export interface AssetViewsDetailDialogProps
	extends
		WithClasses<
			{},
			Omit<AssetViewsDetailProps, 'view' | 'onNext' | 'onPrevious' | 'total'>
		>,
		React.PropsWithRef<{
			DialogProps?: Partial<DialogProps>;
			// asset: IAsset;
			// loading?: boolean;
			open: boolean;
			onClose: () => void;
		}> {}

export const AssetViewsDetailDialogInner = (
	{
		classes: _classes,
		className,
		index,
		open,
		onClose,
		DialogProps,
		...props
	}: AssetViewsDetailDialogProps,
	ref?: AssetViewsDetailDialogRef,
) => {
	const ownerState = {classes: _classes};
	// const classes = useUtilityClasses(ownerState);

	const {asset, loading, data, downloadView} = useAssetViewsProvider();

	const [_index, setIndex] = React.useState(index);
	const total = data.length;

	const nextView = React.useCallback(() => {
		setIndex((prev) => Math.min(prev + 1, total - 1));
	}, [total]);

	const previousView = React.useCallback(() => {
		setIndex((prev) => Math.max(prev - 1, 0));
	}, []);

	const view: IAssetView | undefined = data[_index];

	const initialValues = useMemo(() => {
		return {
			name: view?.name ?? '',
			description: unescapeDelimiters(view?.description ?? ''),
		};
	}, [view]);

	const {actions} = useAssetViewActions({
		asset: props.asset,
		view,
		omit: ['asset-view-edit'],
	});

	const {form, onSubmit} = useAssetViewForm({
		initialValues,
		submit: async (data) => {
			const _assetView = {
				...view,
				...data,
			};
			const uuid = await setViewData({assetView: _assetView});
			dispatcher
				.trigger('app:pollBatchJob', uuid, AssetViewEvent.EDIT, false, [
					_assetView.imageId,
					_assetView,
				])
				.trigger(`${AssetViewEvent.EDIT}:success`, _assetView);

			onClose();
		},
	});

	return (
		<AssetViewsDetailDialogRoot
			aria-label="asset views"
			disableContentPadding
			id="asset-views"
			fullWidth
			loading={loading || !view || !asset}
			fullScreenWhenMobile
			maxWidth="md"
			open={open}
			onClose={onClose}
			{...DialogProps}
			title={view?.name ?? ''}
			DialogTitleProps={{
				actions,
			}}
			controls={
				<>
					<IconButton
						icon={faAngleLeft}
						onClick={previousView}
						label={i18n.t('i18n.previous')}
						disabled={_index === 0}
					/>

					<IconButton
						icon={faAngleRight}
						onClick={nextView}
						label={i18n.t('i18n.next')}
						disabled={_index === total - 1}
					/>
				</>
			}
			ref={ref}
		>
			<AssetViewsDetail
				{...props}
				asset={asset}
				form={form}
				view={view}
				index={_index}
				total={total}
				onNext={nextView}
				onPrevious={previousView}
				onSubmit={onSubmit}
			/>
		</AssetViewsDetailDialogRoot>
	);
};

export const AssetViewsDetailDialogPrimitive = React.forwardRef(
	AssetViewsDetailDialogInner,
) as (
	props: AssetViewsDetailDialogProps,
	ref?: AssetViewsDetailDialogRef,
) => JSX.Element;

export const AssetViewsDetailDialog = AssetViewsDetailDialogPrimitive;
